import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { linksApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './Links.css';
import UpgradePrompt from '../../components/UpgradePrompt';

// Helper function to format dates from various formats
const formatDate = (dateValue) => {
  if (!dateValue) return 'N/A';
  
  try {
    // Log the type and value for debugging
    console.log('Date value type:', typeof dateValue, dateValue);
    
    // Case 1: Firebase Timestamp object with toDate method
    if (typeof dateValue === 'object' && dateValue.toDate && typeof dateValue.toDate === 'function') {
      return dateValue.toDate().toLocaleDateString();
    }
    
    // Case 2: Date object
    if (dateValue instanceof Date) {
      return dateValue.toLocaleDateString();
    }
    
    // Case 3: Timestamp as seconds and nanoseconds (Firestore format)
    if (typeof dateValue === 'object' && dateValue.seconds !== undefined && dateValue.nanoseconds !== undefined) {
      return new Date(dateValue.seconds * 1000 + Math.round(dateValue.nanoseconds / 1000000)).toLocaleDateString();
    }
    
    // Case 4: Timestamp with just seconds
    if (typeof dateValue === 'object' && dateValue.seconds !== undefined) {
      return new Date(dateValue.seconds * 1000).toLocaleDateString();
    }
    
    // Case 5: Firebase server timestamp that might be returned as a map
    if (typeof dateValue === 'object' && Object.keys(dateValue).length > 0) {
      // Try to create a date from various possible properties
      if (dateValue._seconds !== undefined) {
        return new Date(dateValue._seconds * 1000).toLocaleDateString();
      }
    }
    
    // Case 6: ISO string or timestamp number
    if (typeof dateValue === 'string' || typeof dateValue === 'number') {
      const date = new Date(dateValue);
      // Check if the date is valid
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
    }
    
    // Default fallback - stringified representation
    if (typeof dateValue === 'object') {
      return JSON.stringify(dateValue);
    }
    
    return String(dateValue) || 'Unknown date format';
  } catch (err) {
    console.error('Error formatting date:', err, dateValue);
    return 'Invalid date';
  }
};

const LinkList = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeType, setUpgradeType] = useState('click');

  const { user } = useAuth();
  const isFreeUser = user?.plan === 'free';
  const linkLimitReached = isFreeUser && links.length >= 3;

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        setLoading(true);
        setError(null); // Clear any previous errors
        
        const response = await linksApi.getLinks();
        if (response.data.status === 'success') {
          // Access the links array from the standardized response structure with fallback
          setLinks(response.data.data?.links || []);
        } else {
          throw new Error('Failed to fetch links');
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching links:', err);
        // Provide more specific error messages based on the error type
        if (err.response?.status === 500) {
          setError('Server error. Our team has been notified and is working on the issue.');
        } else if (err.response?.status === 401 || err.response?.status === 403) {
          setError('Authentication error. Please try logging in again.');
        } else if (err.response?.data?.message) {
          setError(`Error: ${err.response.data.message}`);
        } else if (err.message) {
          setError(`Error: ${err.message}`);
        } else {
          setError('Failed to load affiliate links. Please try again later.');
        }
        setLoading(false);
      }
    };

    fetchLinks();
  }, []);

  const handleCopyLink = (trackingUrl) => {
    navigator.clipboard.writeText(trackingUrl)
      .then(() => toast.success('Tracking link copied to clipboard'))
      .catch(() => toast.error('Failed to copy link'));
  };

  const handleDeleteLink = async (id) => {
    if (window.confirm('Are you sure you want to delete this link?')) {
      try {
        const response = await linksApi.deleteLink(id);
        
        // Handle successful deletion - check for multiple possible success formats
        // This handles both the standardized API format and direct Firebase responses
        if (response?.data?.status === 'success' || response?.status === 200 || response?.status === 204) {
          setLinks(links.filter(link => link.id !== id));
          toast.success('Link deleted successfully');
          return;
        }
        
        // If we get here, the response wasn't clearly a success, but didn't throw an error
        // We'll still assume success if we got a response without error
        setLinks(links.filter(link => link.id !== id));
        toast.success('Link deleted successfully');
      } catch (err) {
        console.error('Error deleting link:', err);
        
        // Show upgrade prompt if backend blocks due to limits
        const backendMsg = err.response?.data?.message || '';
        if (backendMsg.toLowerCase().includes('click limit')) {
          setUpgradeType('click');
          setShowUpgradePrompt(true);
        } else if (backendMsg.toLowerCase().includes('conversion limit')) {
          setUpgradeType('conversion');
          setShowUpgradePrompt(true);
        } else if (err.response?.status === 500) {
          toast.error('Server error. Please try again later.');
        } else if (err.response?.status === 404) {
          toast.error('Link not found. It may have been already deleted.');
          // Remove from UI anyway since it doesn't exist
          setLinks(links.filter(link => link.id !== id));
        } else {
          // More robust error message handling
          const errorMessage = err.response?.data?.message || err.message || 'Failed to delete link';
          toast.error(errorMessage);
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading affiliate links...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error}</p>
        <div>
          <button className="btn" onClick={() => window.location.reload()}>
            Try Again
          </button>
          <Link to="/dashboard" className="btn btn-secondary ml-2">
            Return to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (links.length === 0) {
    return (
      <div className="empty-container">
        <h2>No Affiliate Links</h2>
        <p>You haven't created any affiliate links yet.</p>
        <Link to="/dashboard/links/create" className="btn">
          Create Your First Link
        </Link>
      </div>
    );
  }

  return (
    <div className="link-list-page">
      {showUpgradePrompt && (
        <UpgradePrompt type={upgradeType} onClose={() => setShowUpgradePrompt(false)} />
      )}
      <div className="page-header">
        <h1>Your Affiliate Links</h1>
        <button
          className="btn"
          disabled={linkLimitReached}
          onClick={() => {
            if (linkLimitReached) {
              setUpgradeType('click');
              setShowUpgradePrompt(true);
            } else {
              window.location.href = `${process.env.PUBLIC_URL}/dashboard/links/create`;
            }
          }}
          style={linkLimitReached ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
        >
          Create New Link
        </button>
      </div>
      {linkLimitReached && (
        <div className="upgrade-message" style={{background:'#fff3cd',border:'1px solid #ffeeba',color:'#856404',padding:'16px',borderRadius:'8px',margin:'16px 0',fontWeight:'500'}}>
          <span>
            <strong>Link Limit Reached:</strong> Free users can only create up to 3 links.<br/>
            To create more links, please <a href="/upgrade" style={{color:'#007bff',textDecoration:'underline'}}>upgrade to a paid plan</a>.
          </span>
        </div>
      )}
      <div className="link-list-container">
        {links.map(link => {
          const baseUrl = window.location.origin;
          const trackingUrl = `${baseUrl}/t/${link.trackingId}`;
          
          return (
            <div key={link.id} className="link-card">
              <div className="link-header">
                <h2>{link.name}</h2>
                <span className="link-date">
                  Created: {formatDate(link.createdAt)}
                </span>
              </div>
              
              <div className="link-details">
                <div className="link-url">
                  <strong>Tracking URL:</strong>
                  <div className="url-display">
                    <span className="url-text">{trackingUrl}</span>
                    <button 
                      className="btn-icon" 
                      onClick={() => handleCopyLink(trackingUrl)}
                      title="Copy to clipboard"
                    >
                      📋
                    </button>
                  </div>
                </div>
                
                <div className="link-url">
                  <strong>Destination:</strong>
                  <span className="url-text">{link.destinationUrl}</span>
                </div>
              </div>
              
              <div className="link-stats">
                <div className="stat">
                  <span className="stat-value">{link.clickCount || 0}</span>
                  <span className="stat-label">Clicks</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{link.conversionCount || 0}</span>
                  <span className="stat-label">Conversions</span>
                </div>
                <div className="stat">
                  <span className="stat-value">
                    {link.clickCount > 0 
                      ? ((link.conversionCount / link.clickCount) * 100).toFixed(1) 
                      : '0.0'}%
                  </span>
                  <span className="stat-label">Conversion Rate</span>
                </div>
                <div className="stat">
                  <span className="stat-value">${(link.revenue || 0).toFixed(2)}</span>
                  <span className="stat-label">Revenue</span>
                </div>
              </div>
              
              <div className="link-actions">
                <Link to={`/dashboard/links/${link.id}`} className="btn btn-sm">
                  View Details
                </Link>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDeleteLink(link.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LinkList;
